import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Layout, LoadingScreen, ErrorNotice } from '../components/Layout';
import { api, apiUrl, ApiClientError } from '../lib/api';
import type { Appointment } from '../lib/types';

interface Calendar { id: string; name: string; primary: boolean; timezone: string; }
interface Duplicate { candidateId: string; score: number; reasons: string[]; }

export function CalendarPage() {
  const { id = '' } = useParams();
  const calendars = useQuery({
    queryKey: ['calendars'],
    queryFn: () => api<{ data: Calendar[] }>('/api/calendars').then((r) => r.data),
  });
  const appointment = useQuery({
    queryKey: ['appointment', id],
    queryFn: () => api<{ data: Appointment }>(`/api/appointments/${id}`).then((r) => r.data),
  });
  const [calendarId, setCalendarId] = useState('primary');
  const [dayBefore, setDayBefore] = useState(true);
  const [twoHours, setTwoHours] = useState(true);
  const [customMinutes, setCustomMinutes] = useState('');
  const [duplicates, setDuplicates] = useState<Duplicate[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const selectedIds = useMemo(() => {
    const group = appointment.data?.groupItems ?? [];
    const selected = group.filter((item) => item.selectedForCreation && !item.googleEventId).map((item) => item.id);
    return group.length ? selected : [id];
  }, [appointment.data?.groupItems, id]);
  const keyFor = (appointmentId: string) => {
    const storageKey = `nadmai:idempotency:${appointmentId}`;
    const existing = sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const created = crypto.randomUUID(); sessionStorage.setItem(storageKey, created); return created;
  };
  async function submitCalendarEvent(forceDuplicate: boolean) {
    if (forceDuplicate) setDuplicates([]);
    setCreateError(null);
    setIsSubmitting(true);
    try {
      const reminders = [
        ...(dayBefore ? [{ method: 'popup' as const, minutes: 1440 }] : []),
        ...(twoHours ? [{ method: 'popup' as const, minutes: 120 }] : []),
        ...(customMinutes ? [{ method: 'popup' as const, minutes: Number(customMinutes) }] : []),
      ];
      for (const appointmentId of selectedIds) {
        const response = await fetch(apiUrl(`/api/appointments/${appointmentId}/calendar-event`), {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'Idempotency-Key': keyFor(appointmentId) },
          body: JSON.stringify({ calendarId, reminders: reminders.length ? reminders : [{ method: 'popup', minutes: 120 }], forceDuplicate }),
        });
        if (!response.ok) {
          const body = await response.json().catch(() => null) as { error?: { code?: string; message?: string; details?: unknown } } | null;
          throw new ApiClientError(response.status, body?.error?.code ?? 'REQUEST_FAILED', body?.error?.message ?? 'สร้างกิจกรรมไม่สำเร็จ กรุณาลองใหม่', body?.error?.details);
        }
      }
      window.location.assign(`/appointments/${id}/success`);
    } catch (error) {
      if (error instanceof ApiClientError && error.code === 'POSSIBLE_DUPLICATE') {
        const detail = error.details as { duplicates?: Duplicate[] } | undefined;
        setDuplicates(detail?.duplicates ?? []);
      } else {
        setCreateError(error instanceof Error ? error.message : 'สร้างกิจกรรมไม่สำเร็จ กรุณาลองใหม่');
      }
    } finally {
      setIsSubmitting(false);
    }
  }
  if (calendars.isLoading || appointment.isLoading) return <Layout title="เลือกปฏิทิน"><LoadingScreen /></Layout>;
  return (
    <Layout title="ปฏิทินและการแจ้งเตือน">
      <section className="mx-auto max-w-lg space-y-5">
        {calendars.isError && <ErrorNotice message="โหลดรายการปฏิทินไม่สำเร็จ กรุณาเชื่อมต่อ Google ใหม่" />}
        <div className="card">
          <label className="label" htmlFor="calendar">บันทึกลงปฏิทิน</label>
          <select id="calendar" className="field" value={calendarId} onChange={(event) => setCalendarId(event.target.value)}>
            {(calendars.data ?? [{ id: 'primary', name: 'ปฏิทินหลัก', primary: true, timezone: 'Asia/Bangkok' }]).map((calendar) => (
              <option key={calendar.id} value={calendar.id}>{calendar.name}{calendar.primary ? ' (หลัก)' : ''}</option>
            ))}
          </select>
        </div>
        {selectedIds.length > 1 && <div className="rounded-xl bg-teal-50 p-3 text-sm text-teal-900">ระบบจะสร้าง {selectedIds.length} รายการที่คุณเลือกจากเอกสารนี้</div>}
        {selectedIds.length === 0 && <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900">ยังไม่ได้เลือกรายการที่จะสร้าง กรุณากลับไปหน้าตรวจสอบ</div>}
        <div className="card space-y-3">
          <h2 className="font-semibold">แจ้งเตือน</h2>
          <label className="flex min-h-12 items-center gap-3"><input type="checkbox" className="h-5 w-5 accent-teal-700" checked={dayBefore} onChange={(e) => setDayBefore(e.target.checked)} /> ก่อนนัด 1 วัน</label>
          <label className="flex min-h-12 items-center gap-3"><input type="checkbox" className="h-5 w-5 accent-teal-700" checked={twoHours} onChange={(e) => setTwoHours(e.target.checked)} /> ก่อนนัด 2 ชั่วโมง</label>
          <label><span className="label">กำหนดเอง (นาทีก่อนนัด)</span><input className="field" type="number" min="0" max="40320" value={customMinutes} onChange={(e) => setCustomMinutes(e.target.value)} placeholder="เช่น 30" /></label>
        </div>
        {duplicates.length > 0 && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
            <h2 className="font-bold text-amber-900">พบรายการที่อาจซ้ำ</h2>
            {duplicates.map((duplicate) => (
              <div key={duplicate.candidateId} className="mt-3 text-sm text-amber-900">
                <Link className="font-semibold underline" to={`/appointments/${duplicate.candidateId}`}>เปิดรายการเดิม</Link>
                <span> — {duplicate.reasons.join(', ')}</span>
              </div>
            ))}
            <button className="btn-secondary mt-4 w-full" onClick={() => void submitCalendarEvent(true)} disabled={isSubmitting}>ยืนยันสร้างรายการใหม่</button>
          </div>
        )}
        {createError && <ErrorNotice message={createError} />}
        <button className="btn-primary w-full" onClick={() => void submitCalendarEvent(false)} disabled={isSubmitting || calendars.isError || selectedIds.length === 0}>{isSubmitting ? 'กำลังสร้างกิจกรรม…' : selectedIds.length > 1 ? `สร้าง ${selectedIds.length} รายการ` : 'เพิ่มใน Google Calendar'}</button>
        <p className="text-center text-xs text-slate-500">กิจกรรมจะมี visibility แบบ private และจะไม่สร้างจนกว่าคุณกดปุ่มนี้</p>
      </section>
    </Layout>
  );
}
